import { Button } from '@sitewise/ui';
import { useEffect, useRef, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';

interface Props {
  /** Called with a PNG Blob once the user accepts the signature. */
  onAccept: (blob: Blob) => void;
  onClear?: () => void;
  /** Set true once the parent has uploaded the signature. */
  uploaded?: boolean;
}

/**
 * Touch-friendly signature pad used in vendor sign-off and per-unit sign-off
 * flows. Captures pen / finger input on tablets and exports a PNG that the
 * caller uploads as a `work_order_signature` attachment.
 */
export function SignaturePad({ onAccept, onClear, uploaded }: Props) {
  const ref = useRef<SignatureCanvas | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [empty, setEmpty] = useState(true);
  const [width, setWidth] = useState(320);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width ?? 320;
      setWidth(Math.max(280, Math.floor(w)));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const handleClear = () => {
    ref.current?.clear();
    setEmpty(true);
    onClear?.();
  };

  const handleAccept = async () => {
    const canvas = ref.current?.getCanvas();
    if (!canvas || ref.current?.isEmpty()) return;
    canvas.toBlob((blob) => {
      if (blob) onAccept(blob);
    }, 'image/png');
  };

  return (
    <div ref={wrapperRef} className="space-y-2">
      <div className="rounded-md border border-border bg-surface">
        <SignatureCanvas
          ref={(r) => {
            ref.current = r;
          }}
          penColor="#111"
          canvasProps={{
            width,
            height: 180,
            className: 'block touch-none rounded-md',
          }}
          onBegin={() => setEmpty(false)}
        />
      </div>
      <div className="flex gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={handleClear} className="min-h-11">
          Clear
        </Button>
        <Button
          type="button"
          size="sm"
          onClick={() => void handleAccept()}
          disabled={empty || uploaded}
          className="min-h-11"
        >
          {uploaded ? 'Signature saved' : 'Use this signature'}
        </Button>
      </div>
    </div>
  );
}
